import { match } from "ts-pattern";

import type {
  FormContextData,
  FormContextMiniForm,
  UUID,
} from "@thalamos/aspire-common";

import { userIsRedoAuthorForTeam } from "../helpers/formContextLogic.js";
import { hasNoActiveWorkItemOrRejectedCancelledOrCompleted } from "../helpers/workItemHelpers.js";

/**
 * True when an admission is read-only for redo: a redo's statuses stay visible
 * but no accept/decline may be taken and the author is never routed into the
 * preview. An admission is read-only once it is closed or has left active
 * workflow.
 */
export function isAdmissionReadOnlyForRedo(
  formContext: Pick<FormContextData, "status" | "activeTeamworkWorkItem">,
): boolean {
  return (
    formContext.status === "closed" ||
    hasNoActiveWorkItemOrRejectedCancelledOrCompleted(formContext)
  );
}

export interface DirectRedoRequest {
  form: FormContextMiniForm;
  /** Reasons the requester selected from the list, as display labels. */
  reasons: string[];
  /** Free-text detail the requester added, if any. */
  additionalInfo?: string;
  /** Name of the user who raised the request, shown in the request banner. */
  requesterName?: string;
}

/**
 * The redo the given user is expected to accept/decline in this admission, if
 * any. A form qualifies only when it is in the "redo-requested" holding state
 * AND carries a *direct* redo work item (one raised on that form) — a form that
 * merely inherited the state from an upstream redo has no work item and is not
 * actioned here. The user must also be the redo author, per
 * `userIsRedoAuthorForTeam`.
 */
export function findDirectRedoRequestForUser(
  formContext: Pick<FormContextData, "forms" | "workItems">,
  userId: UUID,
  userTeamId: UUID | undefined,
): DirectRedoRequest | undefined {
  for (const form of formContext.forms) {
    if (form.redoState !== "redo-requested") continue;

    const workItem = formContext.workItems.find(
      (w) => w.formId === form.id && w.requestType === "redo",
    );
    if (!workItem) continue;

    if (!userIsRedoAuthorForTeam(form, userId, userTeamId)) continue;

    return {
      form,
      reasons: workItem.requestReasons?.map((r) => r.label) ?? [],
      additionalInfo: workItem.requestAdditionalInfo ?? undefined,
      requesterName: workItem.createdByUser.name,
    };
  }
  return undefined;
}

/**
 * The warning-banner copy for a redo request, kept in one place so every surface
 * that shows it uses identical wording. Selected reasons and free-text detail are
 * shown on their own lines when both are present.
 */
export function buildRedoRequestBannerBody({
  requesterName,
  reasons,
  additionalInfo,
}: {
  requesterName?: string;
  reasons: string[];
  additionalInfo?: string;
}): string[] {
  const requestedBy = requesterName ? ` by ${requesterName}` : "";
  const reasonLines = reasons.length
    ? [reasons.join(", "), ...(additionalInfo ? [additionalInfo] : [])]
    : [additionalInfo ?? "No reason provided"];

  return [
    `A redo of this form has been requested${requestedBy} for the following reason:`,
    ...reasonLines,
    "Accepting reopens the form for redo and creates a new form for you to complete. The original is voided once the new form is signed.",
  ];
}

/**
 * The situations a form under redo can be in, as far as its status-pill tooltip
 * is concerned. A med rec is only ever redone directly (redo propagates
 * downstream to its linked application, never up to the med rec), so the
 * direct/inherited distinction applies only to an application's redo request: it
 * separates the application's own request from one inherited from a linked med
 * rec being redone. A void form shows its "Void" label rather than a tooltip, so
 * there is no void situation.
 */
export type RedoTooltipSituation =
  | { type: "med-rec-redo-requested" }
  | { type: "application-redo-requested-direct" }
  | { type: "application-redo-requested-inherited" }
  | { type: "med-rec-redo-in-progress" }
  | { type: "application-redo-in-progress" };

/**
 * The status-pill tooltip explaining why a form under redo cannot yet be linked
 * onward, kept in one place so every surface uses identical wording. The copy is
 * type-aware: a med rec is linked onward to an application, an application to an
 * H3.
 */
export function buildRedoStatusTooltip(
  situation: RedoTooltipSituation,
): string {
  return match(situation)
    .with(
      { type: "med-rec-redo-requested" },
      () =>
        "A user has requested a redo for this Med Rec. This Med Rec cannot be linked to an Application until the redo is completed or retracted.",
    )
    .with(
      { type: "application-redo-requested-direct" },
      () =>
        "A user has requested a redo for this Application. This Application cannot be linked to an H3 until the redo is completed or retracted.",
    )
    .with(
      { type: "application-redo-requested-inherited" },
      () =>
        "A user has requested a redo for a Med Rec linked to this Application. This Application cannot be linked to an H3 until the redo is completed or retracted.",
    )
    .with(
      { type: "med-rec-redo-in-progress" },
      () =>
        "Redo in progress. This Med Rec cannot be linked to an Application until the redo is completed or cancelled.",
    )
    .with(
      { type: "application-redo-in-progress" },
      () =>
        "Redo in progress. This Application cannot be linked to an H3 until the redo is completed or cancelled.",
    )
    .exhaustive();
}
